import firebase from 'firebase/app';

class AdvancedImageOptimizer {
  
  // Configuration for different image types
  static CONFIG = {
    JPEG: {
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 0.85,
      progressive: true
    },
    PNG: {
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 0.9,
      preserveTransparency: true
    },
    WEBP: {
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 0.8,
      lossless: false
    }
  };

  // Maximum file size (3MB)
  static MAX_FILE_SIZE = 3 * 1024 * 1024;
  
  // Supported image types
  static SUPPORTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  /**
   * Advanced image optimization with quality preservation
   * @param {File} file - The image file to optimize
   * @returns {Promise<Blob>} - Optimized image blob
   */
  static async optimizeImage(file) {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
    
          
          // Get image metadata - use file.type instead of img.type
          const { width, height } = img;
          const imageType = file.type || 'image/jpeg'; // Fallback to JPEG if type is undefined
          const config = this.getConfigForType(imageType);
          
          
          
          // Calculate optimal dimensions while preserving aspect ratio
          const { newWidth, newHeight } = this.calculateOptimalDimensions(width, height, config);
          
          
          
          // Set canvas dimensions
          canvas.width = newWidth;
          canvas.height = newHeight;

          // Enable image smoothing for better quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          
          
          // Draw image with high quality
          ctx.drawImage(img, 0, 0, newWidth, newHeight);
          
          // Convert to blob with optimal settings
          const mimeType = this.getOptimalMimeType(imageType);
          const quality = this.getOptimalQuality(imageType, file.size);
          
          console.log('🔧 Optimization settings:', {
            originalType: imageType,
            targetMimeType: mimeType,
            quality: quality,
            originalSize: this.formatFileSize(file.size)
          });
          
          console.log('🔄 Converting to blob...');
          
          canvas.toBlob(
            (blob) => {
              console.log('📦 Blob callback received:', { blob: !!blob, type: blob?.type, size: blob?.size });
              
              if (blob) {
                console.log('✅ Optimization result:', {
                  targetMimeType: mimeType,
                  actualMimeType: blob.type,
                  originalSize: this.formatFileSize(file.size),
                  optimizedSize: this.formatFileSize(blob.size)
                });
                
                // If the optimized image is larger than original, use original
                if (blob.size > file.size) {
                  console.log('📈 Optimized image larger than original, using original');
                  resolve(file);
                } else {
                  console.log(`📉 Optimized: ${this.formatFileSize(file.size)} → ${this.formatFileSize(blob.size)}`);
                  resolve(blob);
                }
              } else {
                console.error('❌ Blob creation failed');
                reject(new Error('Failed to optimize image - blob is null'));
              }
            },
            mimeType,
            quality
          );
        } catch (error) {
          console.error('❌ Error in image optimization:', error);
          reject(error);
        }
      };

      img.onerror = () => {
        console.error('❌ Image failed to load');
        reject(new Error('Failed to load image'));
      };
      
      try {
        console.log('🔗 Creating object URL for image...');
        const objectUrl = URL.createObjectURL(file);
        console.log('🔗 Object URL created:', objectUrl);
        img.src = objectUrl;
      } catch (error) {
        console.error('❌ Error creating object URL:', error);
        reject(new Error('Failed to create object URL: ' + error.message));
      }
    });
  }

  /**
   * Calculate optimal dimensions while preserving aspect ratio
   */
  static calculateOptimalDimensions(width, height, config) {
    let newWidth = width;
    let newHeight = height;

    // Calculate aspect ratio
    const aspectRatio = width / height;

    // Resize if image is too large
    if (width > config.maxWidth || height > config.maxHeight) {
      if (aspectRatio > 1) {
        // Landscape
        newWidth = Math.min(width, config.maxWidth);
        newHeight = newWidth / aspectRatio;
      } else {
        // Portrait
        newHeight = Math.min(height, config.maxHeight);
        newWidth = newHeight * aspectRatio;
      }
    }

    // Ensure dimensions are integers
    newWidth = Math.round(newWidth);
    newHeight = Math.round(newHeight);

    return { newWidth, newHeight };
  }

  /**
   * Get configuration for specific image type
   */
  static getConfigForType(type) {
    // Ensure type is a string and handle undefined/null cases
    if (!type || typeof type !== 'string') {
      console.warn('Image type is undefined or invalid, using JPEG configuration');
      return this.CONFIG.JPEG;
    }
    
    const lowerType = type.toLowerCase();
    if (lowerType.includes('png')) {
      return this.CONFIG.PNG;
    } else if (lowerType.includes('webp')) {
      return this.CONFIG.WEBP;
    } else {
      return this.CONFIG.JPEG;
    }
  }

  /**
   * Get optimal MIME type for the image
   */
  static getOptimalMimeType(originalType) {
    // Ensure type is valid
    if (!originalType || typeof originalType !== 'string') {
      console.warn('Original type is undefined or invalid, using JPEG');
      return 'image/jpeg';
    }
    
    // Prefer WebP for better compression if supported
    if (this.isWebPSupported()) {
      console.log('WebP is supported, converting to WebP for better compression');
      return 'image/webp';
    }
    
    console.log('WebP not supported, using original format:', originalType);
    // Fall back to original type
    return originalType;
  }

  /**
   * Check if WebP is supported
   */
  static isWebPSupported() {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      const webpDataURL = canvas.toDataURL('image/webp');
      const isSupported = webpDataURL.indexOf('data:image/webp') === 0;
      console.log('WebP support check:', isSupported ? 'Supported' : 'Not supported');
      return isSupported;
    } catch (error) {
      console.warn('Error checking WebP support:', error);
      return false;
    }
  }

  /**
   * Get optimal quality based on file size and type
   */
  static getOptimalQuality(type, fileSize) {
    // Ensure fileSize is valid
    if (!fileSize || fileSize <= 0) {
      console.warn('File size is invalid, using default quality');
      return 0.8;
    }
    
    const config = this.getConfigForType(type);
    let quality = config.quality;

    // Adjust quality based on file size
    if (fileSize > 2 * 1024 * 1024) { // > 2MB
      quality = Math.max(0.7, quality - 0.1);
    } else if (fileSize > 1 * 1024 * 1024) { // > 1MB
      quality = Math.max(0.75, quality - 0.05);
    }

    return quality;
  }

  /**
   * Validate image file with detailed checks
   */
  static validateImage(file) {
    if (!file) {
      return { success: false, message: 'No file selected' };
    }

    // Handle undefined file type
    if (!file.type) {
      console.warn('File type is undefined, attempting to proceed with JPEG');
      // Don't fail validation for undefined type, let it proceed
    } else if (!this.SUPPORTED_TYPES.includes(file.type)) {
      return { 
        success: false, 
        message: 'Unsupported file type. Please use JPEG, PNG, or WebP images.' 
      };
    }

    if (file.size > this.MAX_FILE_SIZE) {
      return { 
        success: false, 
        message: `File size must be less than ${this.MAX_FILE_SIZE / (1024 * 1024)}MB` 
      };
    }

    return { success: true, message: 'File is valid' };
  }

  /**
   * Upload optimized image to Firebase Storage
   */
  static async uploadOptimizedImage(file, questionId) {
    try {
      // Debug logging
      console.log('Uploading image:', {
        name: file.name,
        type: file.type,
        size: this.formatFileSize(file.size),
        questionId: questionId
      });

      // Validate file
      const validation = this.validateImage(file);
      if (!validation.success) {
        throw new Error(validation.message);
      }

      // Optimize image
      const optimizedBlob = await this.optimizeImage(file);
      
      // Create consistent filename based on image content and question ID
      const fileExtension = this.getFileExtension(optimizedBlob.type);
      
      // Generate filename based on question ID and image hash for consistency
      // This prevents duplicate uploads of the same image
      const imageHash = await this.generateImageHash(optimizedBlob);
      const fileName = `image-questions/${questionId}/${imageHash}.${fileExtension}`;
      
      // Upload to default bucket with correct path structure
      const storageRef = firebase.storage().ref();
      const fileRef = storageRef.child(fileName);
      
      // Use default bucket with proper path structure for image-questions
      try {
        // Upload to default bucket with the correct path: image-questions/{questionId}/{filename}
        const defaultFileRef = storageRef.child(fileName);
          
        // Set metadata for better caching
        const metadata = {
          cacheControl: 'public, max-age=31536000', // 1 year cache
          contentType: optimizedBlob.type,
          customMetadata: {
            originalSize: file.size.toString(),
            optimizedSize: optimizedBlob.size.toString(),
            questionId: questionId,
            optimizedAt: new Date().toISOString(),
            bucket: 'default'
          }
        };
        
        const snapshot = await defaultFileRef.put(optimizedBlob, metadata);
        const downloadURL = await snapshot.ref.getDownloadURL();
        
        console.log(`✅ Image uploaded to default bucket: ${fileName}`);
        
        return {
          url: downloadURL,
          originalSize: file.size,
          optimizedSize: optimizedBlob.size,
          compressionRatio: ((file.size - optimizedBlob.size) / file.size * 100).toFixed(1),
          bucket: 'default'
        };
      } catch (uploadError) {
        console.error('❌ Failed to upload image:', uploadError);
        throw uploadError;
      }
    } catch (error) {
      console.error('Error uploading optimized image:', error);
      throw error;
    }
  }

  /**
   * Get file extension from MIME type
   */
  static getFileExtension(mimeType) {
    // Ensure mimeType is valid
    if (!mimeType || typeof mimeType !== 'string') {
      console.warn('MIME type is undefined or invalid, using jpg extension');
      return 'jpg';
    }
    
    const extensions = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp'
    };
    return extensions[mimeType] || 'jpg';
  }

  /**
   * Delete image from Firebase Storage
   */
  static async deleteImage(imageUrl) {
    try {
      if (!imageUrl || !imageUrl.includes('firebase')) {
        return;
      }

      const url = new URL(imageUrl);
      const path = decodeURIComponent(url.pathname.split('/o/')[1]?.split('?')[0] || '');
      
      if (path) {
        const storageRef = firebase.storage().ref();
        const fileRef = storageRef.child(path);
        await fileRef.delete();
        console.log('Image deleted from Firebase Storage:', path);
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Generate a hash for the image blob to ensure unique filenames
   * This prevents duplicate uploads of the same image content
   */
  static async generateImageHash(blob) {
    try {
      // Convert blob to array buffer
      const arrayBuffer = await blob.arrayBuffer();
      
      // Create a simple hash using the array buffer
      const hashArray = Array.from(new Uint8Array(arrayBuffer));
      let hash = 0;
      
      // Simple hash function (djb2 algorithm)
      for (let i = 0; i < hashArray.length; i++) {
        hash = ((hash << 5) - hash + hashArray[i]) & 0xffffffff;
      }
      
      // Convert to positive hex string and take first 8 characters
      const hashString = Math.abs(hash).toString(16).substring(0, 8);
      
      // Add timestamp to ensure uniqueness even for identical images
      const timestamp = Date.now().toString(36);
      
      return `${hashString}_${timestamp}`;
    } catch (error) {
      console.error('Error generating image hash:', error);
      // Fallback to timestamp-based hash
      return `img_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    }
  }

  /**
   * Get image metadata
   */
  static async getImageMetadata(file) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          size: file.size || 0,
          type: file.type || 'image/jpeg', // Fallback to JPEG if type is undefined
          aspectRatio: img.width / img.height
        });
      };
      img.onerror = () => {
        resolve({
          size: file.size || 0,
          type: file.type || 'image/jpeg', // Fallback to JPEG if type is undefined
          error: 'Could not load image metadata'
        });
      };
      img.src = URL.createObjectURL(file);
    });
  }
}

export default AdvancedImageOptimizer; 